/**
 * The JSON shapes the serializer moves between, and the three failure modes it
 * can report.
 *
 * These mirror ProseMirror's own JSON representation rather than wrapping it.
 * The canonical column is `document.content` (§5.1), written by the editor and
 * read by the collab server; a bespoke intermediate representation here would
 * be a fourth format nobody asked for.
 */

export interface MarkJSON {
  type: string;
  attrs?: Record<string, unknown>;
}

export interface NodeJSON {
  type: string;
  attrs?: Record<string, unknown>;
  content?: NodeJSON[];
  marks?: MarkJSON[];
  text?: string;
}

/** A whole document — always a `doc` node, per the schema's top node. */
export interface DocumentJSON extends NodeJSON {
  type: 'doc';
}

/**
 * The JSON does not fit the closed component set (§5.1) — an unknown node type,
 * a mark the schema does not define, or content that violates a content
 * expression.
 *
 * ADR 0002: "a serializer bug is not a display glitch — it is data corruption
 * on the way in." Every entry point validates against the schema so that a
 * malformed document fails at the boundary rather than three writes later.
 */
export class DocumentSchemaError extends Error {
  override readonly name = 'DocumentSchemaError';
}

/**
 * The document is schema-valid but holds something markdown cannot represent —
 * a merged table cell, a table without a header row.
 *
 * Thrown rather than silently dropped. §5.1's whole argument for a closed
 * component set is that "once round-tripping breaks, markdown becomes a lossy
 * export and AI legibility degrades" — silently, which is why this is loud.
 */
export class MarkdownSerializeError extends Error {
  override readonly name = 'MarkdownSerializeError';
}

/** Markdown that no reading of the §5.1 dialect produces a document from. */
export class MarkdownParseError extends Error {
  override readonly name = 'MarkdownParseError';
}
